/**
 * Rebuilds a storage connection's service folders when its account-info
 * snapshot arrives (the tree factory runs synchronously at connect time,
 * before `azure-storage:account-info` answers). Keeps the pinned run and the
 * expansion state of folders that survive the rebuild. S7 review D F8: a File
 * Shares folder whose loaded children were built for another File availability
 * (e.g. "No file shares yet" before the answer said the identity must name its
 * shares) loses them, and an expanded one loads again at once — through the explorer's shared query client
 * (`setStorageTreeQueryClient`, review E), so the reload shares the explorer's listing cache.
 */
import type { QueryClient } from '@tanstack/react-query';
import { buildStorageConnectionChildren } from '../../explorer/models/azureStorageNodeFactories';
import type { ConnectionTreeNode, DatabaseExplorerNode, StorageServiceFolderNode } from '../../explorer/models/nodeTypes';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import { useDatabaseStore } from '../store/databaseStore';
import { getStorageAccountInfoSnapshot } from '../store/storageAccountInfoStore';
import { getStorageFallbackQueryClient, loadStorageServiceFolder } from './azureStorageListing';
import { fileFolderChildrenOutdated } from './storageFileFolderState';
import { withHnsBadges } from '../../workspace/storage-explorer/hns/hnsBadges';

export const PINNED_NODE_TYPES: readonly string[] = ['pinned-database', 'pinned-schema', 'pinned-folder', 'pinned-object', 'pinned-workspace', 'pinned-storage'];

export function isPinnedNodeType(type: string): boolean {
  return PINNED_NODE_TYPES.includes(type);
}

/** The app's query client, registered by `useStorageAccountInfoSync` (the explorer); the private fallback serves only without it. */
let sharedQueryClient: QueryClient | undefined;
export function setStorageTreeQueryClient(client: QueryClient | undefined): void {
  sharedQueryClient = client;
}
/** The client storage tree listings use (the app's, else the private fallback) — for callers outside the tree hooks. */
export function getStorageTreeQueryClient(): QueryClient {
  return sharedQueryClient ?? getStorageFallbackQueryClient();
}

export function rebuildStorageConnectionChildren(connectionId: string): void {
  const store = useDatabaseStore.getState();
  const connNode = store.treeData.find(
    (node): node is ConnectionTreeNode => node.type === 'connection' && (node as ConnectionTreeNode).metadata.id === connectionId,
  );
  if (!connNode || connNode.metadata.engine !== 'azurestorage') return;
  const existing = (connNode.children ?? []) as DatabaseExplorerNode[];
  const pinned = existing.filter((child) => isPinnedNodeType(child.type));
  const previousById = new Map(existing.map((child) => [child.id, child]));
  const rebuilt = buildStorageConnectionChildren({
    connectionId,
    parentId: connNode.id,
    connection: getConnectionById(connectionId).connection,
    accountInfo: getStorageAccountInfoSnapshot(connectionId),
  }).map((child) => {
    const previous = previousById.get(child.id);
    if (previous && fileFolderChildrenOutdated(previous)) return { ...child, isExpanded: previous.isExpanded, children: [], isLoading: false } as DatabaseExplorerNode;
    // S8: containers kept from before the account info arrived get their HNS badge now.
    const children = previous?.children ? withHnsBadges(previous.children as DatabaseExplorerNode[], getStorageAccountInfoSnapshot(connectionId)?.hns) : undefined;
    return previous ? ({ ...child, isExpanded: previous.isExpanded, children, isLoading: previous.isLoading } as DatabaseExplorerNode) : child;
  });
  store.updateNode(connNode.id, { children: [...pinned, ...rebuilt] });
  for (const child of rebuilt) {
    const previous = previousById.get(child.id);
    if (!previous?.isExpanded || !fileFolderChildrenOutdated(previous)) continue;
    void loadStorageServiceFolder({ node: child as StorageServiceFolderNode, queryClient: sharedQueryClient ?? getStorageFallbackQueryClient(), updateNode: store.updateNode, forceReload: true });
  }
}
